/**
 * Slider Widget
 *
 * Range slider for percentage or value selection
 */

import type { WidgetProps } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { Card, CardContent } from '@tonnie/ui';
import React, { useCallback, useId, useState } from 'react';

interface ISliderConfig {
  title?: string;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  initialValue?: number;
  labels?: {
    min?: string;
    max?: string;
  };
}

type ISliderWidgetProps = WidgetProps & {
  config?: ISliderConfig;
};

export function SliderWidget({ config, onComplete }: ISliderWidgetProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const min = config?.min || 0;
  const max = config?.max || 100;
  const step = config?.step || 1;
  const unit = config?.unit || '%';
  const [value, setValue] = useState<number>(
    config?.initialValue ?? Math.round((max - min) / 2 + min)
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setValue(newValue);
  }, []);

  const handleSubmit = useCallback(() => {
    onComplete?.(value);
  }, [onComplete, value]);

  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <Card className="border-border/50 shadow-sm" data-testid="widget-slider">
      <CardContent className="pt-6">
        {config?.title && (
          <h3 id={titleId} className="text-base font-semibold mb-2">
            {config.title}
          </h3>
        )}
        {config?.description && (
          <p className="text-sm text-muted-foreground mb-4">{config.description}</p>
        )}

        <div className="space-y-6">
          <div className="text-center" data-testid="widget-slider-value">
            <span className="text-4xl font-bold text-primary-accessible">{value}</span>
            <span className="text-lg text-muted-foreground ml-1">{unit}</span>
          </div>

          <div className="relative px-2">
            <input
              type="range"
              // Named by the widget's own title; a slider the brain sent untitled is the answer.
              aria-labelledby={config?.title ? titleId : undefined}
              aria-label={config?.title ? undefined : t('copilot.aria.answerInput')}
              min={min}
              max={max}
              step={step}
              value={value}
              onChange={handleChange}
              data-testid="widget-slider-handle"
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary [&]:bg-[linear-gradient(to_right,var(--primary)_0%,var(--primary)_var(--slider-progress),var(--muted)_var(--slider-progress),var(--muted)_100%)]"
              style={{ '--slider-progress': `${percentage}%` } as React.CSSProperties}
            />
          </div>

          {(config?.labels?.min || config?.labels?.max) && (
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {config.labels?.min || min}
                {unit}
              </span>
              <span>
                {config.labels?.max || max}
                {unit}
              </span>
            </div>
          )}

          <button
            onClick={handleSubmit}
            data-testid="widget-slider-submit"
            className="w-full py-3 px-4 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {t('widgets.continue')}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export default SliderWidget;
